import { useEffect, useRef } from "react";

//#region Analytics IDS
const GA_ID = 'G-6QGZCZYMVG';
const GA_COOKIE = `_ga_${GA_ID.replace(/^G-/, "")}`;

function readCookie(name) {
    const m = document.cookie.match(
        new RegExp("(?:^|; )" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "=([^;]*)")
    );
    return m ? decodeURIComponent(m[1]) : null;
}

function getSessionIdFromCookie() {
    const raw = readCookie(GA_COOKIE);
    if (!raw) return null;

    const gs2 = raw.match(/(?:^|[.$])s(\d{9,})/);
    if (gs2) return gs2[1];

    const p = raw.split(".");
    return /^\d+$/.test(p[2] || "") ? p[2] : null;
}

function getClientIdFromCookie() {
    const raw = readCookie("_ga");
    if (!raw) return null;
    const p = raw.split(".");
    return p.length >= 4 ? p.slice(-2).join(".") : null;
}

function gtagGet(field, timeoutMs = 5000) {
    return new Promise((resolve) => {
        if (typeof window === "undefined" || !window.gtag) return resolve(null);
        const t = setTimeout(() => resolve(null), timeoutMs);
        try {
            window.gtag("get", GA_ID, field, (val) => {
                clearTimeout(t);
                return resolve(val ?? null);
            });
        } catch (e) {
            clearTimeout(t);
            return resolve(null);
        }
    });
}

export async function getGaIds() {
    const [c, s] = await Promise.all([gtagGet("client_id"), gtagGet("session_id")]);
    return {
        client_id: c || getClientIdFromCookie(),
        session_id: s || getSessionIdFromCookie(),
    };
}
//#region LogAnalytics

// logAnalyticsOnce.js

const STORAGE_KEY = "logged_events";
const TTL_MS = 90 * 24 * 60 * 60 * 1000;

// synchronous guard: covers StrictMode double-invoke and same-tick races,
// which localStorage alone can't catch (both reads happen before either write)
const inMemory = new Set();

function readStore() {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    } catch (e) {
        return {};
    }
}

function writeStore(store) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch (e) { /* private mode, quota */ }
}

function emit(eventName, params) {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return false;
    try {
        window.gtag("event", eventName, { ...params, send_to: GA_ID });
        return true;
    } catch (e) {
        return false;
    }
}

export function logAnalyticsSafe(eventName, params = {}) {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return false;
    try {
        window.gtag("event", eventName, { ...params, send_to: GA_ID });
        return true;
    } catch (e) {
        return false;
    }
}

export function logAnalyticsOnce(eventName, orderId, params = {}) {
    if (!orderId) return false;
    const key = `${eventName}:${orderId}`;

    if (inMemory.has(key)) return false;
    inMemory.add(key);

    const store = readStore();
    if (store[key]) return false;

    const sent = emit(eventName, params);
    if (!sent) {
        inMemory.delete(key);   // gtag wasn't there — allow a later attempt
        return false;
    }

    const cutoff = Date.now() - TTL_MS;
    for (const k of Object.keys(store)) {
        if (store[k] < cutoff) delete store[k];
    }
    store[key] = Date.now();
    writeStore(store);
    return true;
}


export function useLogOnce(eventName, orderId, params) {
    const fired = useRef(false);
    useEffect(() => {
        console.log("Log once", orderId);
        if (fired.current || !orderId) return;
        fired.current = true;
        logAnalyticsOnce(eventName, orderId, params);
    }, [eventName, orderId]);
}